import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
    useContext,
} from 'react';
import PropTypes from 'prop-types';
import { CommunityContext } from '../../../../contexts/ContextCreator';
import AlarmClockIcon from '../../../Icons/AlarmClock/AlarmClockIcon';
import AllPostsIcon from '../../../Icons/AllPosts/AllPostsIcon';
import ExpandIcon from '../../../Icons/Expand/ExpandIcon';
import LikesIcon from '../../../Icons/Likes/LikesIcon';
import TrendingIcon from '../../../Icons/Trending/TrendingIcon';
import RankingButton from './RankingButton/RankingButton';
import './RankingComponent.scss';
import { EmitMetric } from '../../../Analytics/VisibilityPixel/VisibilityPixel';

const RankingComponent = ({ setIsLoading }) => {
    const nodeRef = useRef();
    const timeoutRef = useRef();
    const { sort, setSort } = useContext(CommunityContext);
    const [menuOpen, setMenuOpen] = useState(false);

    const dropDownLabel = {
        DEFAULT: 'All Posts',
        TRENDING: 'Trending posts',
        RECENT: 'Recent posts',
        NEED_LOVE: 'Posts that need love',
    };

    const closeDropdown = () => {
        clearTimeout(timeoutRef.current);
        setMenuOpen(false);
    };

    const setNewSort = (type) => {
        if (type !== sort) {
            setIsLoading(false, true);
            setSort(type, setIsLoading);
        }
    };

    const handleSortMenuClick = useCallback(
        (e) => {
            if (nodeRef?.current.contains(e.target)) {
                if (!menuOpen) {
                    EmitMetric({
                        misc_event_type: 'community-sort-arrow-clicked',
                        misc_event_count: 1,
                    });
                }
                clearTimeout(timeoutRef.current);
                setMenuOpen(true);
                timeoutRef.current = setTimeout(() => {
                    if (menuOpen) {
                        setMenuOpen(false);
                    }
                }, 3000);
                return;
            }
            closeDropdown();
        },
        [menuOpen]
    );

    useEffect(() => {
        document.addEventListener('mousedown', handleSortMenuClick);
        return () => {
            document.removeEventListener('mousedown', handleSortMenuClick);
        };
    }, [handleSortMenuClick]);

    return (
        <div className="component-Content-Feed-RankingComponent">
            <div className="component-Content-Feed-RankingComponent-content">
                Showing:&nbsp;
                <b>{sort && dropDownLabel[sort]}</b>
                <div ref={nodeRef}>
                    <button
                        type="button"
                        className="rankingMenu-button"
                        onClick={handleSortMenuClick}
                    >
                        <ExpandIcon isActive={menuOpen} />
                    </button>
                    {menuOpen && (
                        <div className="rankingMenu-content">
                            <div className="rankingMenu-item">
                                <RankingButton
                                    className="all-posts"
                                    type="DEFAULT"
                                    buttonLabel="All Posts"
                                    icon={AllPostsIcon}
                                    isActive={sort === 'DEFAULT'}
                                    setSort={setNewSort}
                                    closeDropdown={closeDropdown}
                                />
                            </div>
                            <div className="rankingMenu-item">
                                <RankingButton
                                    type="TRENDING"
                                    buttonLabel="Trending posts"
                                    icon={TrendingIcon}
                                    isActive={sort === 'TRENDING'}
                                    setSort={setNewSort}
                                    closeDropdown={closeDropdown}
                                />
                            </div>
                            <div className="rankingMenu-item">
                                <RankingButton
                                    type="RECENT"
                                    buttonLabel="Most recent posts"
                                    icon={AlarmClockIcon}
                                    isActive={sort === 'RECENT'}
                                    setSort={setNewSort}
                                    closeDropdown={closeDropdown}
                                />
                            </div>
                            <div className="rankingMenu-item">
                                <RankingButton
                                    type="NEED_LOVE"
                                    buttonLabel="Posts that need love"
                                    icon={LikesIcon}
                                    isActive={sort === 'NEED_LOVE'}
                                    setSort={setNewSort}
                                    closeDropdown={closeDropdown}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

RankingComponent.protoTypes = {
    setIsLoading: PropTypes.func.isRequired,
};

export default RankingComponent;
